import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMessagePlacementHooks, usePostOfficeContext } from '@atlassian/post-office-context';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data/placement-fetcher-provider';
import type { SerializableRecord } from '@atlassian/post-office-placement-data/types';
import { useFetchPlacementData } from '@atlassian/post-office-placement-data/useFetchPlacementData';

import { PostOfficeMessage } from '../components/PostOfficeMessage';
import type { PostOfficeMessageProps } from '../components/PostOfficeMessage';

/**
 * Minimum shape required to parameterise {@link PlacementMessage}.
 *
 * Every message carries a `messageTemplateId` (used as a discriminant) and a
 * `context` object. Pass a union of these to {@link usePlacementData} to get
 * narrowable message types:
 *
 * ```ts
 * type MyMessages =
 *   | { messageTemplateId: 'banner'; context: { title: string } }
 *   | { messageTemplateId: 'flag';   context: { flagKey: string } };
 * ```
 */
export type PlacementMessageDiscriminant<TContext extends SerializableRecord = SerializableRecord> =
	{
		messageTemplateId: string;
		context: TContext;
	};

/** An error entry returned by the `/headless` endpoint for a single message that failed to render on the backend. */
export type PlacementDataEndpointError = {
	messageInstanceId: string;
	messageTemplateId: string;
	reason: string;
};

/**
 * A single message item returned by the Placement `/headless` endpoint.
 *
 * Mirrors the backend `EligibleMessage` shape. Parameterise with a union of
 * `{ messageTemplateId, context }` discriminants to model multiple template
 * types in one placement:
 *
 * ```ts
 * type MyMessages =
 *   | { messageTemplateId: 'banner'; context: { title: string } }
 *   | { messageTemplateId: 'flag';   context: { flagKey: string } };
 *
 * // data.messages is Array<PlacementMessage<MyMessages>>
 * // — narrows correctly when you switch on message.messageTemplateId
 * const { data } = usePlacementData<MyMessages>('some-placement');
 * ```
 *
 * @typeParam TMessage - A `{ messageTemplateId, context }` shape (or union thereof).
 */
export type PlacementMessage<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = {
	messageInstanceId: string;
	messageCategory: 'Transactional' | 'Marketing' | 'Engagement';
	createdAt: string;
	eventTime: string;
	triggerId: string;
	messageCreationType?: string;
	recommendationSession?: SerializableRecord;
	readStatus?: string;
	enrichedAttributes?: SerializableRecord;
	journeyId?: string;
	journeyTriggerId?: string;
	configSetId?: string;
} & TMessage;

/**
 * Shape of the `data` field in the Placement `/headless` response.
 *
 * Contains a `messages` array of fully-typed {@link PlacementMessage} items,
 * optionally extended with additional top-level fields via `TExtension`.
 *
 * @typeParam TMessage - A `{ messageTemplateId, context }` shape (or union thereof)
 *   used to type each message item. See {@link PlacementMessage}.
 * @typeParam TExtension - Additional fields to merge into the data shape.
 *   Defaults to `Record<never, never>` (no extra fields — a no-op intersection).
 */
export type PlacementDataEndpointData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
> = { messages: Array<PlacementMessage<TMessage>> } & TExtension;

/**
 * Raw HTTP response body produced by the Placement `/headless` endpoint.
 *
 * Used on the **backend** when annotating the response body in `create-placement`.
 * For frontend consumption, use {@link PlacementDataResult} instead — it is
 * parameterized by the full data shape rather than the message discriminant.
 *
 * @typeParam TMessage - A `{ messageTemplateId, context }` shape (or union thereof).
 */
export type PlacementDataEndpointBody<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = {
	data: PlacementDataEndpointData<TMessage>;
	errors: Array<PlacementDataEndpointError>;
};

/**
 * Full response envelope returned by the Placement `/headless` endpoint.
 *
 * This is the **frontend-facing** counterpart to {@link PlacementDataEndpointBody}.
 * Pass this as `TData` when calling {@link usePlacementData} to get typed
 * access to the `data` field.
 *
 * @typeParam TData - Shape of the `data` field. Must extend {@link PlacementDataEndpointData}.
 */
export type PlacementDataResult<
	TData extends PlacementDataEndpointData = PlacementDataEndpointData,
> = {
	data: TData;
	errors: Array<PlacementDataEndpointError>;
};

/**
 * Return type of {@link usePlacementData}.
 *
 * @typeParam TData - Shape of the `data` field in the Placement `/headless` response.
 */
/**
 * A `PostOfficeMessage` wrapper pre-bound to the placement's `placementId`.
 * `messageInstanceId`, `messageTemplateId`, and `messageCategory` must still be supplied per-message,
 * but `placementId` is already closed over.
 */
export type PostOfficePlacementMessageType = (
	props: Omit<PostOfficeMessageProps, 'placementId'>,
) => JSX.Element | null;

/**
 * Selects the data source the headless API hook fetches from.
 *
 * - `'PRODUCTION'` (default) — fetches from the production Post Office gateway
 *   (`/gateway/api/post-office/api/v1/placements/`).
 * - `'DEV_SERVER'` — fetches from the local placement dev server started by
 *   `yarn placement:v2 dev` (default port `4224`). Useful during local development
 *   to test placement data without deploying. The dev server exposes the same URL
 *   path as production but at `http://localhost:4224`.
 *
 * This is a string union rather than a boolean so that additional sources (e.g.
 * `'STAGING'`) can be added in future without a breaking change.
 */
export type PlacementDataSource = 'PRODUCTION' | 'DEV_SERVER';

/**
 * Options accepted by {@link usePlacementData}.
 */
export type UsePlacementDataOptions = {
	/**
	 * Which data source to fetch from. Defaults to `'PRODUCTION'`.
	 * Pass `'DEV_SERVER'` to point the hook at the local placement dev server
	 * (`yarn placement:v2 dev`, default port `4224`).
	 */
	dataSource?: PlacementDataSource;
};

export type UsePlacementDataResult<
	TData extends PlacementDataEndpointData = PlacementDataEndpointData,
> = {
	/**
	 * The raw, unfiltered response from the Placement API.
	 * Use `dismissedIds` to filter out dismissed messages before rendering.
	 */
	data: TData | undefined;
	/** Per-message backend errors returned by the `/headless` endpoint. */
	errors: Array<PlacementDataEndpointError>;
	/**
	 * The set of messageInstanceIds that have been dismissed in this session.
	 * Updated immediately via pub/sub when `dismissMessage` is called — no refetch needed.
	 *
	 * **Reference stability:** A new Set is only created when the contents change.
	 * Dismissing the same ID twice does not produce a new Set reference.
	 */
	dismissedIds: ReadonlySet<string>;
	/** Whether the initial Placement API fetch is in progress. */
	isLoading: boolean;
	/** Any error returned by the Placement API fetch. */
	error: Error | undefined;
	/**
	 * Dismisses a message instance by its ID.
	 * Immediately updates `dismissedIds` via pub/sub and notifies the Post Office backend.
	 *
	 * @param messageInstanceId - The ID of the message instance to dismiss.
	 */
	dismissMessage: (messageInstanceId: string) => void;
	/**
	 * A `PostOfficeMessage` wrapper with `placementId` already closed over.
	 * Pass `messageInstanceId`, `messageTemplateId`, `messageCategory`, and `children` per message.
	 */
	PostOfficePlacementMessage: PostOfficePlacementMessageType;
};

/**
 * Foundation hook for the Post Office Headless API.
 *
 * Fetches raw Placement API data for a given `placementId` and tracks which
 * message instances have been dismissed in the current session via the Post
 * Office pub/sub system.
 *
 * All higher-level hooks (`useAllMessages`, `useMessageInstances`, `useMessageData`) compose on top
 * of this hook.
 *
 * Automatically reads the current `PostOfficeContext` (product, locale, tenantId, workspaceAri,
 * etc.) via `usePostOfficeContext` and forwards it to the Placement API request as query
 * parameters. No manual context threading is required.
 *
 * Requires `PlacementFetchingProvider` and `MessagePlacementProvider` (or
 * `PostOfficeProvider`) to be present in the React tree.
 *
 * @example
 * ```tsx
 * const { data, dismissedIds, isLoading, error, dismissMessage } = usePlacementData(
 *   'some-placement',
 * );
 *
 * const visibleMessages = data?.messages.filter(
 *   m => !dismissedIds.has(m.messageInstanceId)
 * ) ?? [];
 * ```
 *
 * @typeParam TMessage - A {@link PlacementMessageDiscriminant} or union thereof.
 *   Provide a union to model multiple template types with narrowable `context`:
 *   `usePlacementData<{ messageTemplateId: 'banner'; context: BannerCtx } | { messageTemplateId: 'flag'; context: FlagCtx }>(id)`
 * @typeParam TExtension - Additional top-level fields to expect in the `data` shape.
 * @param placementId - The ID of the placement to fetch data for. Defaults to `'headless'`.
 * @param options - Optional configuration. Pass `{ dataSource: 'DEV_SERVER' }` to point the hook
 *   at the local placement dev server (`yarn placement:v2 dev`, default port `4224`).
 * @see https://hello.atlassian.net/wiki/spaces/POSTOFFICE/pages/6764207116
 */

/** Base URL used when `dataSource` is `'DEV_SERVER'`. Only included in non-production builds. */
const DEV_SERVER_BASE_URL = 'http://localhost:4224';

export const usePlacementData = <
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	placementId = 'headless',
	options?: UsePlacementDataOptions,
): UsePlacementDataResult<PlacementDataEndpointData<TMessage, TExtension>> => {
	const fetcher = usePlacementFetchingContext();
	const { onMessageDismiss, dismissMessageFromPlacement } = useMessagePlacementHooks();
	const postOfficeContext = usePostOfficeContext();

	const { dataSource = 'PRODUCTION' } = options ?? {};

	// When targeting the dev server, override the fetch so the request goes to
	// `http://localhost:4224` instead of the production gateway. The dev server
	// exposes the same URL path as production.
	let effectiveFetcher: typeof fetcher = fetcher;
	if (dataSource === 'DEV_SERVER') {
		effectiveFetcher = {
			fetch: (url: string, init?: RequestInit) => {
				// Replace the origin with the dev server base URL, keeping the path intact.
				// Guard against SSR environments where window is not defined.
				const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
				const originalUrl = new URL(url, origin);
				const devUrl = `${DEV_SERVER_BASE_URL}${originalUrl.pathname}${originalUrl.search}`;
				return fetcher.fetch(devUrl, init);
			},
		};
	}

	const response = useFetchPlacementData<
		PlacementDataResult<PlacementDataEndpointData<TMessage, TExtension>>,
		SerializableRecord
	>(placementId, 'headless', postOfficeContext, effectiveFetcher, undefined, {
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
	});

	const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());

	// Synchronously reset dismissedIds in the render path when placementId changes.
	// A useEffect-based reset fires after paint, leaving a one-render window where
	// the new placement is painted with stale dismissed IDs from the previous one.
	// Calling setState during render causes React to discard this render and
	// immediately re-render with the new state; consumers never observe stale IDs.
	//
	// Infinite re-render safety — two independent guards prevent looping:
	//   1. prevPlacementIdRef.current is written BEFORE setDismissedIds is called.
	//      On the re-render React immediately triggers, the ref already equals
	//      placementId so the outer `if` is false — no further setState occurs.
	//   2. After the reset, dismissedIds.size === 0, so the inner size guard is
	//      also false — a second guard independent of the ref. Either alone is
	//      sufficient; both together make the loop prevention unconditional.
	// See: 'infinite re-render safety' tests in usePlacementData.test.tsx.
	const prevPlacementIdRef = useRef(placementId);
	const placementChanged = prevPlacementIdRef.current !== placementId;

	// Return an empty set immediately on the first render with a new placementId —
	// including the discarded render that React produces when setState is called
	// during rendering. This ensures no render ever exposes stale dismissed IDs
	// from the previous placement, even transiently.
	// Reference stability: when dismissedIds is already empty we reuse the same
	// Set reference (no new object, no unnecessary re-render).
	const effectiveDismissedIds =
		placementChanged && dismissedIds.size > 0 ? new Set<string>() : dismissedIds;

	if (placementChanged) {
		prevPlacementIdRef.current = placementId;
		// Only reset if the set is non-empty to avoid an unnecessary extra render.
		if (dismissedIds.size > 0) {
			setDismissedIds(new Set());
		}
	}

	// Subscribe to pub/sub dismiss events on mount, unsubscribe on unmount.
	useEffect(() => {
		const unsubscribe = onMessageDismiss(({ messageInstanceId }) => {
			setDismissedIds((prev) => {
				// Guard: do not create a new Set reference if the ID is already dismissed.
				if (prev.has(messageInstanceId)) {
					return prev;
				}
				return new Set([...prev, messageInstanceId]);
			});
		});

		return unsubscribe;
	}, [onMessageDismiss]);

	const dismissMessage = useCallback(
		(messageInstanceId: string) => {
			dismissMessageFromPlacement({ messageInstanceId });
		},
		[dismissMessageFromPlacement],
	);

	const { isLoading, error } = response;

	const PostOfficePlacementMessage = useMemo<PostOfficePlacementMessageType>(
		() =>
			({
				messageInstanceId,
				messageTemplateId,
				messageCategory,
				triggerId,
				messageCreationType,
				journeyId,
				journeyTriggerId,
				configSetId,
				children,
			}: Omit<PostOfficeMessageProps, 'placementId'>) => (
				<PostOfficeMessage
					messageInstanceId={messageInstanceId}
					messageTemplateId={messageTemplateId}
					placementId={placementId}
					messageCategory={messageCategory}
					triggerId={triggerId}
					messageCreationType={messageCreationType}
					journeyId={journeyId}
					journeyTriggerId={journeyTriggerId}
					configSetId={configSetId}
				>
					{children}
				</PostOfficeMessage>
			),
		[placementId],
	);

	return {
		data: response.data?.data,
		errors: response.data?.errors ?? [],
		dismissedIds: effectiveDismissedIds,
		isLoading,
		error,
		dismissMessage,
		PostOfficePlacementMessage,
	};
};
